import React, { useState, useEffect } from 'react';
import { Key, Users, TrendingUp, AlertTriangle, Plus, Settings, Trash2, X, Search, Copy, CheckCircle } from 'lucide-react';
import { AdminHeader } from '../../../shared/components';
import { adminApiService } from '../../../shared/services/adminApi';
import { adminAuthService } from '../../../shared/services/adminAuth';
import toast from 'react-hot-toast';

interface ApiKey {
  keyId: string;
  name: string;
  userId: {
    _id: string;
    email: string;
    name: string;
    plan: string;
  };
  permissions: string[];
  isActive: boolean;
  lastUsed?: string;
  expiresAt?: string;
  createdAt: string;
}

interface ApiUsage {
  keyId: string;
  period: {
    start: string;
    end: string;
  };
  usage: Array<{
    totalRequests: number;
    successfulRequests: number;
    errorRequests: number;
    _id: {
      year: number;
      month: number;
      day: number;
    };
  }>;
}

interface User {
  _id: string;
  email: string;
  name: string;
  plan: string;
}

interface CreateApiKeyData {
  userId: string;
  name: string;
  permissions: string[];
  expiresAt?: string;
}

const ApiManagementPage: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiUsage, setApiUsage] = useState<Record<string, ApiUsage>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // 모달 관련 상태
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  
  // 편집 모달 상태
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState<ApiKey | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // 삭제 확인 모달 상태
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingApiKey, setDeletingApiKey] = useState<ApiKey | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // 폼 데이터
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [keyName, setKeyName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(['url:create', 'url:read']);
  const [expirationDate, setExpirationDate] = useState('');
  
  // 편집 폼 데이터
  const [editKeyName, setEditKeyName] = useState('');
  const [editPermissions, setEditPermissions] = useState<string[]>([]);
  const [editIsActive, setEditIsActive] = useState(true);
  
  // 사용자 검색
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const availablePermissions = [
    { id: 'url:create', name: 'URL 생성', description: '단축 URL 생성 권한' },
    { id: 'url:read', name: 'URL 조회', description: '단축 URL 조회 권한' },
    { id: 'url:update', name: 'URL 수정', description: '단축 URL 수정 권한' },
    { id: 'url:delete', name: 'URL 삭제', description: '단축 URL 삭제 권한' },
    { id: 'analytics:read', name: '분석 조회', description: '클릭 분석 데이터 조회 권한' },
  ];

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      
      const token = adminAuthService.getToken();
      if (!token) {
        toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }
      
      const response = await fetch('/api/admin/api-keys', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setApiKeys(data.data.apiKeys);
        // 각 API 키의 사용량 조회
        data.data.apiKeys.forEach((key: ApiKey) => {
          fetchApiUsage(key.keyId);
        });
        setLastUpdated(new Date());
      } else {
        toast.error(data.message || 'API 키 목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('API 키 목록 조회 실패:', error);
      toast.error('API 키 목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await fetchApiKeys();
    } catch (error) {
      console.error('새로고침 실패:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchApiUsage = async (keyId: string) => {
    try {
      const token = adminAuthService.getToken();
      if (!token) {return;}
      
      const response = await fetch(`/api/admin/api-keys/usage/${keyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setApiUsage(prev => ({
          ...prev,
          [keyId]: data.data
        }));
      }
    } catch (error) {
      console.error('API 사용량 조회 실패:', error);
    }
  };

  // 사용자 검색 함수
  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const data = await adminApiService.searchUsers(query);
      setSearchResults(data.users);
    } catch (error) {
      console.error('사용자 검색 실패:', error);
      toast.error('사용자 검색에 실패했습니다');
    } finally {
      setIsSearching(false);
    }
  };

  // API 키 이름 자동 생성 함수
  const generateApiKeyName = (user: User, permissions?: string[]): string => {
    const userName = user.name || user.email.split('@')[0];
    const existingKeysCount = apiKeys.filter(key => key.userId._id === user._id).length;
    
    // 권한 기반 이름 생성
    if (permissions && permissions.length > 0) {
      const hasReadOnly = permissions.every(p => p.includes('read'));
      const hasFullAccess = permissions.includes('url:create') && permissions.includes('url:update') && permissions.includes('url:delete');
      
      if (hasReadOnly) {
        return `${userName} - Read-Only API Key`;
      } else if (hasFullAccess) {
        return `${userName} - Full Access API Key`;
      } else {
        return `${userName} - Custom API Key`;
      }
    }
    
    // 기본 이름 생성
    if (existingKeysCount === 0) {
      return `${userName} - Production API Key`;
    } else {
      return `${userName} - API Key #${existingKeysCount + 1}`;
    }
  };

  // API 키 생성 함수
  const createApiKey = async () => {
    if (!selectedUser || !keyName.trim()) {
      toast.error('사용자와 키 이름을 입력해주세요');
      return;
    }

    if (selectedPermissions.length === 0) {
      toast.error('최소 하나의 권한을 선택해주세요');
      return;
    }

    try {
      setIsCreating(true);
      const createData: CreateApiKeyData = {
        userId: selectedUser._id,
        name: keyName.trim(),
        permissions: selectedPermissions,
      };

      if (expirationDate) {
        createData.expiresAt = expirationDate;
      }

      const token = adminAuthService.getToken();
      if (!token) {
        toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      const response = await fetch('/api/admin/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createData)
      });
      
      const data = await response.json();
      if (data.success) {
        setCreatedApiKey(data.data.apiKey);
        toast.success('API 키가 성공적으로 생성되었습니다');
        fetchApiKeys(); // 목록 새로고침
      } else {
        toast.error(data.message || 'API 키 생성에 실패했습니다');
      }
    } catch (error) {
      console.error('API 키 생성 실패:', error);
      toast.error('API 키 생성에 실패했습니다');
    } finally {
      setIsCreating(false);
    }
  };

  // 모달 초기화
  const resetModal = () => {
    setIsCreateModalOpen(false);
    setSelectedUser(null);
    setKeyName('');
    setSelectedPermissions(['url:create', 'url:read']);
    setExpirationDate('');
    setUserSearch('');
    setSearchResults([]);
    setCreatedApiKey(null);
  };

  // API 키 복사
  const copyApiKey = async (apiKey: string) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success('API 키가 클립보드에 복사되었습니다');
    } catch (error) {
      toast.error('복사에 실패했습니다');
    }
  };

  // 편집 모달 열기
  const openEditModal = (apiKey: ApiKey) => {
    setEditingApiKey(apiKey);
    setEditKeyName(apiKey.name);
    setEditPermissions([...apiKey.permissions]);
    setEditIsActive(apiKey.isActive);
    setIsEditModalOpen(true);
  };

  // API 키 수정
  const updateApiKey = async () => {
    if (!editingApiKey || !editKeyName.trim()) {
      toast.error('키 이름을 입력해주세요');
      return;
    }

    if (editPermissions.length === 0) {
      toast.error('최소 하나의 권한을 선택해주세요');
      return;
    }

    try {
      setIsUpdating(true);

      const token = adminAuthService.getToken();
      if (!token) {
        toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      const response = await fetch(`/api/admin/api-keys/${editingApiKey.keyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editKeyName.trim(),
          permissions: editPermissions,
          isActive: editIsActive
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('API 키가 성공적으로 수정되었습니다');
        setIsEditModalOpen(false);
        fetchApiKeys(); // 목록 새로고침
      } else {
        toast.error(data.message || 'API 키 수정에 실패했습니다');
      }
    } catch (error) {
      console.error('API 키 수정 실패:', error);
      toast.error('API 키 수정에 실패했습니다');
    } finally {
      setIsUpdating(false);
    }
  };

  // 삭제 확인 모달 열기
  const openDeleteModal = (apiKey: ApiKey) => {
    setDeletingApiKey(apiKey);
    setIsDeleteModalOpen(true);
  };

  // API 키 삭제
  const deleteApiKey = async () => {
    if (!deletingApiKey) {return;}

    try {
      setIsDeleting(true);

      const token = adminAuthService.getToken();
      if (!token) {
        toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      const response = await fetch(`/api/admin/api-keys/${deletingApiKey.keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('API 키가 성공적으로 삭제되었습니다');
        setIsDeleteModalOpen(false);
        fetchApiKeys(); // 목록 새로고침
      } else {
        toast.error(data.message || 'API 키 삭제에 실패했습니다');
      }
    } catch (error) {
      console.error('API 키 삭제 실패:', error);
      toast.error('API 키 삭제에 실패했습니다');
    } finally {
      setIsDeleting(false);
    }
  };

  // 편집 모달 초기화
  const resetEditModal = () => {
    setIsEditModalOpen(false);
    setEditingApiKey(null);
    setEditKeyName('');
    setEditPermissions([]);
    setEditIsActive(true);
  };

  // 삭제 모달 초기화
  const resetDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingApiKey(null);
  };
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Admin Header */}
      <AdminHeader 
        title="API 관리"
        subtitle="사용자 API 키 생성 및 관리 (Growth 플랜 이상 사용자만 API 접근 가능)"
        showRefresh={true}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastUpdated={lastUpdated}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Key className="mr-3" />
              API 키 관리
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Growth 플랜 이상 사용자의 API 키를 관리합니다
            </p>
          </div>
          
          <button 
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            API 키 생성
          </button>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <Key className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">총 API 키</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{apiKeys.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">활성 키</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {apiKeys.filter(key => key.isActive).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">총 요청 수</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.values(apiUsage).reduce((acc, usage) => 
                    acc + (usage.usage?.reduce((dayAcc: number, day: any) => dayAcc + day.totalRequests, 0) || 0), 0
                  )}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">에러 요청</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.values(apiUsage).reduce((acc, usage) => 
                    acc + (usage.usage?.reduce((dayAcc: number, day: any) => dayAcc + day.errorRequests, 0) || 0), 0
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* API 키 목록 */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">API 키 목록</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    키 정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    사용자
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    권한
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : apiKeys.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    생성된 API 키가 없습니다.
                  </td>
                </tr>
              ) : (
                apiKeys.map((apiKey) => (
                  <tr key={apiKey.keyId}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {apiKey.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                          {apiKey.keyId}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {apiKey.userId.name || apiKey.userId.email}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {apiKey.userId.email}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {apiKey.permissions.map((permission) => (
                          <span
                            key={permission}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                          >
                            {permission}
                          </span>
                        ))}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          apiKey.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {apiKey.isActive ? '활성' : '비활성'}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => openEditModal(apiKey)}
                          title="설정"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button 
                          className="text-red-600 hover:text-red-900"
                          onClick={() => openDeleteModal(apiKey)}
                          title="삭제"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* API 키 생성 모달 */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {createdApiKey ? 'API 키 생성 완료' : 'API 키 생성'}
              </h2>
              <button
                onClick={resetModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {createdApiKey ? (
                // API 키 생성 완료 화면
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    API 키가 성공적으로 생성되었습니다!
                  </h3>
                  <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                    ⚠️ 보안상 이 API 키는 지금만 표시됩니다. 안전한 곳에 저장해주세요.
                  </p>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                        {createdApiKey}
                      </code>
                      <button
                        onClick={() => copyApiKey(createdApiKey)}
                        className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="text-left bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">API 키 정보:</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <li><strong>사용자:</strong> {selectedUser?.email}</li>
                      <li><strong>키 이름:</strong> {keyName}</li>
                      <li><strong>권한:</strong> {selectedPermissions.join(', ')}</li>
                      {expirationDate && <li><strong>만료일:</strong> {expirationDate}</li>}
                    </ul>
                  </div>
                </div>
              ) : (
                // API 키 생성 폼
                <div className="space-y-6">
                  {/* 사용자 검색 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      사용자 선택 *
                    </label>
                    {selectedUser ? (
                      <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {selectedUser.name || selectedUser.email}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {selectedUser.email} - {selectedUser.plan} 플랜
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedUser(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                          <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => {
                              setUserSearch(e.target.value);
                              searchUsers(e.target.value);
                            }}
                            placeholder="이메일 또는 이름으로 사용자 검색..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        
                        {searchResults.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                            {searchResults.map((user) => (
                              <button
                                key={user._id}
                                onClick={() => {
                                  setSelectedUser(user);
                                  setKeyName(generateApiKeyName(user)); // 자동 키 이름 생성
                                  setUserSearch('');
                                  setSearchResults([]);
                                }}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {user.name || user.email}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {user.email} - {user.plan} 플랜
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {isSearching && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Growth 플랜 이상의 사용자만 API 키를 생성할 수 있습니다.
                    </p>
                  </div>

                  {/* 키 이름 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API 키 이름 *
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={keyName}
                        onChange={(e) => setKeyName(e.target.value)}
                        placeholder="예: Production API Key"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                      {selectedUser && (
                        <button
                          type="button"
                          onClick={() => setKeyName(generateApiKeyName(selectedUser))}
                          className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500"
                          title="자동 생성"
                        >
                          🔄
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      사용자 선택 시 자동으로 생성됩니다. 수정 가능합니다.
                    </p>
                  </div>

                  {/* 권한 선택 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      권한 선택 *
                    </label>
                    <div className="space-y-2">
                      {availablePermissions.map((permission) => (
                        <label key={permission.id} className="flex items-start">
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={(e) => {
                              let newPermissions;
                              if (e.target.checked) {
                                newPermissions = [...selectedPermissions, permission.id];
                              } else {
                                newPermissions = selectedPermissions.filter(p => p !== permission.id);
                              }
                              setSelectedPermissions(newPermissions);
                              
                              // 권한 변경 시 API 키 이름 자동 업데이트
                              if (selectedUser && newPermissions.length > 0) {
                                setKeyName(generateApiKeyName(selectedUser, newPermissions));
                              }
                            }}
                            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="ml-2">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {permission.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {permission.description}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      💡 권한을 선택하면 API 키 이름이 자동으로 업데이트됩니다.
                    </p>
                  </div>

                  {/* 만료일 (선택사항) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      만료일 (선택사항)
                    </label>
                    <input
                      type="date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      비워두면 만료되지 않는 키가 생성됩니다.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={resetModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                {createdApiKey ? '닫기' : '취소'}
              </button>
              {!createdApiKey && (
                <button
                  onClick={createApiKey}
                  disabled={isCreating || !selectedUser || !keyName.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? '생성 중...' : 'API 키 생성'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* API 키 편집 모달 */}
      {isEditModalOpen && editingApiKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                API 키 편집
              </h2>
              <button
                onClick={resetEditModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 사용자 정보 (읽기 전용) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  사용자
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {editingApiKey.userId.name || editingApiKey.userId.email}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {editingApiKey.userId.email} - {editingApiKey.userId.plan} 플랜
                  </div>
                </div>
              </div>

              {/* 키 이름 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  API 키 이름 *
                </label>
                <input
                  type="text"
                  value={editKeyName}
                  onChange={(e) => setEditKeyName(e.target.value)}
                  placeholder="예: Production API Key"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* 권한 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  권한 선택 *
                </label>
                <div className="space-y-2">
                  {availablePermissions.map((permission) => (
                    <label key={permission.id} className="flex items-start">
                      <input
                        type="checkbox"
                        checked={editPermissions.includes(permission.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditPermissions([...editPermissions, permission.id]);
                          } else {
                            setEditPermissions(editPermissions.filter(p => p !== permission.id));
                          }
                        }}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {permission.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {permission.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* 활성 상태 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  상태
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editIsActive}
                    onChange={(e) => setEditIsActive(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">
                    API 키 활성화
                  </span>
                </label>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  비활성화하면 이 API 키로 요청할 수 없습니다.
                </p>
              </div>

              {/* 키 정보 */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">키 정보</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div><strong>키 ID:</strong> <code className="font-mono">{editingApiKey.keyId}</code></div>
                  <div><strong>생성일:</strong> {new Date(editingApiKey.createdAt).toLocaleString('ko-KR')}</div>
                  {editingApiKey.lastUsed && (
                    <div><strong>마지막 사용:</strong> {new Date(editingApiKey.lastUsed).toLocaleString('ko-KR')}</div>
                  )}
                  {editingApiKey.expiresAt && (
                    <div><strong>만료일:</strong> {new Date(editingApiKey.expiresAt).toLocaleString('ko-KR')}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={resetEditModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                취소
              </button>
              <button
                onClick={updateApiKey}
                disabled={isUpdating || !editKeyName.trim() || editPermissions.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? '수정 중...' : '수정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API 키 삭제 확인 모달 */}
      {isDeleteModalOpen && deletingApiKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md m-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                API 키 삭제 확인
              </h2>
              <button
                onClick={resetDeleteModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-12 h-12 text-red-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    정말로 삭제하시겠습니까?
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    이 작업은 되돌릴 수 없습니다.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">삭제할 API 키</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div><strong>이름:</strong> {deletingApiKey.name}</div>
                  <div><strong>사용자:</strong> {deletingApiKey.userId.email}</div>
                  <div><strong>키 ID:</strong> <code className="font-mono">{deletingApiKey.keyId}</code></div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  ⚠️ <strong>주의:</strong> API 키를 삭제하면 해당 키를 사용하는 모든 애플리케이션이 더 이상 작동하지 않습니다. 
                  관련 사용량 기록도 함께 삭제됩니다.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={resetDeleteModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                취소
              </button>
              <button
                onClick={deleteApiKey}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ApiManagementPage;